import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tabs,
  Tab,
} from "@mui/material";
import { PlayArrow as PlayIcon } from "@mui/icons-material";
import type { TestSet, PipelineStep } from "./types";
import { ALL_STEPS, PIPELINE_STEPS, formatTestSetLabel } from "./types";
import { MonacoJsonEditor } from "../../../components/MonacoJsonEditor";

interface RunProgress {
  resultId: string;
  workflowId: string;
  processedCount: number;
  totalCount: number;
  status: string;
}

interface RunPipelineDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  testSets: TestSet[];
  selectedTestSetId: string;
  setSelectedTestSetId: (id: string) => void;
  emailIds: string;
  setEmailIds: (ids: string) => void;
  stepsToRun: PipelineStep[];
  onStepToggle: (step: PipelineStep) => void;
  running: boolean;
  runProgress: RunProgress | null;
  onRunPipeline: () => void;
  overwriteResults?: boolean;
  setOverwriteResults?: (value: boolean) => void;
  inputMode: "emailIds" | "jsonInputs";
  setInputMode: (mode: "emailIds" | "jsonInputs") => void;
  inputsJson: string;
  setInputsJson: (json: string) => void;
}

export function RunPipelineDialog({
  open,
  onClose,
  title,
  testSets,
  selectedTestSetId,
  setSelectedTestSetId,
  emailIds,
  setEmailIds,
  stepsToRun,
  onStepToggle,
  running,
  runProgress,
  onRunPipeline,
  overwriteResults,
  setOverwriteResults,
  inputMode,
  setInputMode,
  inputsJson,
  setInputsJson,
}: RunPipelineDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={() => !running && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title ?? "Run Pipeline"}</DialogTitle>
      <DialogContent>
        {runProgress ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Processing emails...
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    runProgress.totalCount > 0
                      ? (runProgress.processedCount / runProgress.totalCount) * 100
                      : 0
                  }
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {runProgress.processedCount} / {runProgress.totalCount}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Status: {runProgress.status}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Test Set</InputLabel>
              <Select
                value={selectedTestSetId}
                label="Select Test Set"
                onChange={(e) => {
                  setSelectedTestSetId(e.target.value);
                  if (e.target.value) setEmailIds("");
                }}
              >
                <MenuItem value="">-- None --</MenuItem>
                {testSets.map((ts) => (
                  <MenuItem key={ts.id} value={ts.id}>
                    {formatTestSetLabel(ts)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tabs
              value={inputMode === "emailIds" ? 0 : 1}
              onChange={(_, v) => setInputMode(v === 0 ? "emailIds" : "jsonInputs")}
              sx={{ mb: 2 }}
            >
              <Tab label="Email IDs" />
              <Tab label="JSON Inputs" />
            </Tabs>

            {inputMode === "emailIds" ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Enter email IDs directly (comma or newline separated):
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={emailIds}
                  onChange={(e) => {
                    setEmailIds(e.target.value);
                    if (e.target.value) setSelectedTestSetId("");
                  }}
                  placeholder="Enter email IDs..."
                  disabled={!!selectedTestSetId}
                />
              </>
            ) : (
              <>
                <MonacoJsonEditor
                  value={inputsJson}
                  onChange={setInputsJson}
                  height={200}
                  readOnly={!!selectedTestSetId}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Valid fields: <code>emailId</code>, <code>shipmentId</code>, <code>reminderId</code>, <code>cutOffDate</code>
                </Typography>
              </>
            )}

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Steps to Run
            </Typography>
            <FormGroup row>
              {ALL_STEPS.map((step) => (
                <FormControlLabel
                  key={step}
                  control={
                    <Checkbox
                      checked={stepsToRun.includes(step)}
                      onChange={() => onStepToggle(step)}
                    />
                  }
                  label={PIPELINE_STEPS[step].label}
                />
              ))}
            </FormGroup>

            {setOverwriteResults && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                  Result Options
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={overwriteResults ?? true}
                      onChange={(e) => setOverwriteResults(e.target.checked)}
                    />
                  }
                  label="Overwrite current results (preserves notes)"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                  When checked, the results will replace the current result object.
                  When unchecked, a new result will be created.
                </Typography>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>
          {running ? "Running..." : "Cancel"}
        </Button>
        {!runProgress && (
          <Button
            variant="contained"
            onClick={onRunPipeline}
            disabled={running || (
              inputMode === "emailIds"
                ? (!emailIds.trim() && !selectedTestSetId)
                : !inputsJson.trim()
            )}
            startIcon={running ? <CircularProgress size={16} /> : <PlayIcon />}
          >
            Run Pipeline
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
